import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    PLATFORM_ID,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { formatBytes } from './format-bytes';

export interface RxTxSample {
    timestamp: string | number | Date;
    // null/undefined/NaN means "no data for this bucket" — renders as a gap
    // rather than a zero. Use this when an upstream aggregation legitimately
    // has no value (e.g. derivative of a counter at SA rekey, or an empty
    // date_histogram bucket).
    rx: number | null | undefined;
    tx: number | null | undefined;
}

export interface DowntimeRange {
    start: string | number | Date;
    end: string | number | Date;
}

// Point-in-time event overlaid as a single vertical line at `t`. Used today for
// CHILD_SA rekey events on the IPsec metrics chart; semantically anything "transient
// thing that happened at this instant" the caller wants to call out alongside the
// trend line, without claiming a duration. `label`, if present, becomes the line's
// `title` (browser tooltip) so callers can attach context (e.g. which child rekeyed).
export interface ChartMarker {
    t: string | number | Date;
    label?: string;
}

interface Point {
    date: Date;
    rx: number | null;
    tx: number | null;
}

const COLOR_TX = '#0273fb';
const COLOR_RX = '#08dc5a';
const COLOR_TX_FILL = 'rgba(2, 115, 251, 0.15)';
const COLOR_RX_FILL = 'rgba(8, 220, 90, 0.15)';
const COLOR_AXIS = '#97a0b2';
const COLOR_GRID = '#e8e8e8';
const COLOR_LEGEND_TEXT = '#434c5e';

@Component({
    selector: 'app-rx-tx-timeline',
    templateUrl: './rx-tx-timeline-chart.component.html',
    styleUrls: ['./rx-tx-timeline-chart.component.scss'],
    standalone: false,
})
export class RxTxTimelineChartComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() samples: RxTxSample[] = [];
    @Input() downtimeRanges: DowntimeRange[] = [];
    // Point-in-time markers — rendered as thin amber dashed vertical lines. Distinct
    // from downtimeRanges (red translucent band) so the two can coexist without color
    // conflict: red = "the tunnel was DOWN over this stretch", amber tick = "something
    // transient happened here". Used today for CHILD_SA rekey moments.
    @Input() markers: ChartMarker[] = [];
    @Input() loading = false;
    @Input() height = '14rem';
    @Input() title?: string;
    // Unit drives the y-axis tick + tooltip value formatting. The two-series
    // chart is used for throughput (bytes), system gauges (percent), and
    // liveness (seconds since last packet); 'bytes' is the historical default.
    @Input() unit: 'bytes' | 'percent' | 'seconds' = 'bytes';
    // Per-series labels render in the legend + tooltip. Default Rx/Tx keeps
    // the component's original signature; callers showing other pairs (CPU/Mem,
    // In/Out, etc.) override these.
    @Input() rxLabel = 'Rx';
    @Input() txLabel = 'Tx';

    @ViewChild('chartWrapper') chartWrapper!: ElementRef<HTMLDivElement>;
    @ViewChild('chartSvg') chartSvg!: ElementRef<SVGSVGElement>;
    @ViewChild('tooltip') tooltipEl!: ElementRef<HTMLDivElement>;

    private d3: any;
    private readonly isBrowser: boolean;
    private resizeObserver: ResizeObserver | null = null;
    private points: Point[] = [];

    constructor(@Inject(PLATFORM_ID) platformId: object) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    get hasData(): boolean {
        return this.points.length > 0;
    }

    async ngAfterViewInit() {
        if (!this.isBrowser) return;
        this.d3 = await import('d3');
        this.normalize();
        this.setupResizeObserver();
        this.render();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['samples']) {
            this.normalize();
        }
        if (this.d3) {
            this.render();
        }
    }

    private get parsedDowntime(): { start: Date; end: Date }[] {
        return (this.downtimeRanges ?? [])
            .map(r => ({ start: new Date(r.start as any), end: new Date(r.end as any) }))
            .filter(r => !isNaN(r.start.getTime()) && !isNaN(r.end.getTime()));
    }

    private get parsedMarkers(): { date: Date; label: string | null }[] {
        return (this.markers ?? [])
            .map(m => ({ date: new Date(m.t as any), label: m.label ?? null }))
            .filter(m => !isNaN(m.date.getTime()));
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
    }

    private normalize() {
        this.points = (this.samples ?? [])
            .map((s) => ({
                date: new Date(s.timestamp as any),
                rx: this.numOrNull(s.rx),
                tx: this.numOrNull(s.tx),
            }))
            .filter((p) => !isNaN(p.date.getTime()))
            .sort((a, b) => a.date.getTime() - b.date.getTime());
    }

    // Preserve nulls so d3.line/area .defined() can render them as gaps.
    // Coerce strings to numbers; map NaN / null / undefined to null.
    private numOrNull(v: number | null | undefined): number | null {
        if (v == null) return null;
        const n = +v;
        return Number.isFinite(n) ? n : null;
    }

    private formatValue(v: number): string {
        switch (this.unit) {
            case 'percent':
                return `${v.toFixed(1)}%`;
            case 'seconds':
                return `${v.toFixed(1)}s`;
            case 'bytes':
            default:
                return formatBytes(v);
        }
    }

    private setupResizeObserver() {
        if (!this.chartWrapper?.nativeElement || typeof ResizeObserver === 'undefined') return;
        this.resizeObserver = new ResizeObserver(() => this.render());
        this.resizeObserver.observe(this.chartWrapper.nativeElement);
    }

    private render() {
        if (!this.d3 || !this.chartSvg?.nativeElement) return;
        const d3 = this.d3;
        const svg = d3.select(this.chartSvg.nativeElement);
        svg.selectAll('*').remove();

        if (!this.points.length) return;

        const wrapper = this.chartWrapper.nativeElement;
        const width = wrapper.clientWidth;
        const height = wrapper.clientHeight;
        const margin = { top: this.title ? 36 : 28, right: 20, bottom: 28, left: 56 };
        const innerW = width - margin.left - margin.right;
        const innerH = height - margin.top - margin.bottom;
        if (innerW <= 0 || innerH <= 0) return;

        svg.attr('viewBox', `0 0 ${width} ${height}`);
        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        const x = d3.scaleTime().domain(d3.extent(this.points, (d: Point) => d.date) as [Date, Date]).range([0, innerW]);
        // Filter null values out of the max computation — a bucket with no data
        // shouldn't pull the y-axis range up or down.
        const maxVal = d3.max(this.points, (d: Point) =>
            Math.max(d.tx ?? Number.NEGATIVE_INFINITY, d.rx ?? Number.NEGATIVE_INFINITY)
        ) || 0;
        const y = d3.scaleLinear().domain([0, maxVal * 1.1 || 1]).range([innerH, 0]).nice();

        // Grid
        g.append('g')
            .call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(() => ''))
            .call((sel: any) => sel.selectAll('.domain').remove())
            .call((sel: any) => sel.selectAll('.tick line').attr('stroke', COLOR_GRID));

        // Downtime shading
        for (const range of this.parsedDowntime) {
            const x0 = Math.max(0, x(range.start));
            const x1 = Math.min(innerW, x(range.end));
            if (x1 > x0) {
                g.append('rect')
                    .attr('x', x0)
                    .attr('y', 0)
                    .attr('width', x1 - x0)
                    .attr('height', innerH)
                    .attr('fill', 'rgba(244, 67, 54, 0.12)')
                    .attr('pointer-events', 'none');
                g.append('line')
                    .attr('x1', x0).attr('x2', x0)
                    .attr('y1', 0).attr('y2', innerH)
                    .attr('stroke', 'rgba(244, 67, 54, 0.4)')
                    .attr('stroke-width', 1)
                    .attr('stroke-dasharray', '4,2')
                    .attr('pointer-events', 'none');
                g.append('line')
                    .attr('x1', x1).attr('x2', x1)
                    .attr('y1', 0).attr('y2', innerH)
                    .attr('stroke', 'rgba(244, 67, 54, 0.4)')
                    .attr('stroke-width', 1)
                    .attr('stroke-dasharray', '4,2')
                    .attr('pointer-events', 'none');
            }
        }

        // Point-in-time markers — amber dashed vertical lines. Drawn AFTER the downtime
        // band so markers landing exactly at a recovery edge (full-rebuild signature)
        // sit on top and stay visible; pointer-events off so they don't intercept the
        // chart's hover handlers. The optional <title> child gives a browser tooltip.
        for (const marker of this.parsedMarkers) {
            const xpos = x(marker.date);
            if (xpos < 0 || xpos > innerW) continue;
            const line = g.append('line')
                .attr('x1', xpos).attr('x2', xpos)
                .attr('y1', 0).attr('y2', innerH)
                .attr('stroke', 'rgba(245, 158, 11, 0.7)')
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '3,3')
                .attr('pointer-events', 'none');
            if (marker.label) {
                line.append('title').text(marker.label);
            }
        }

        // X axis
        g.append('g')
            .attr('transform', `translate(0,${innerH})`)
            .call(d3.axisBottom(x).ticks(Math.max(2, Math.min(6, Math.floor(innerW / 110)))).tickSizeOuter(0))
            .call((sel: any) => sel.selectAll('text').attr('fill', COLOR_AXIS).attr('font-size', '0.6875rem'))
            .call((sel: any) => sel.selectAll('.domain, .tick line').attr('stroke', COLOR_GRID));

        // Y axis
        g.append('g')
            .call(d3.axisLeft(y).ticks(4).tickFormat((v: number) => this.formatValue(v)))
            .call((sel: any) => sel.selectAll('text').attr('fill', COLOR_AXIS).attr('font-size', '0.6875rem'))
            .call((sel: any) => sel.selectAll('.domain').remove())
            .call((sel: any) => sel.selectAll('.tick line').attr('stroke', COLOR_GRID));

        // .defined() on the d3 area/line generators causes any null-y point to
        // break the path — producing a visible gap in the chart rather than
        // a misleading drop-to-zero or linear interpolation across the gap.

        // Tx area + line
        g.append('path')
            .datum(this.points)
            .attr('fill', COLOR_TX_FILL)
            .attr(
                'd',
                d3.area()
                    .defined((d: Point) => d.tx != null)
                    .x((d) => x(d.date))
                    .y0(innerH)
                    .y1((d) => y(d.tx as number))
                    .curve(d3.curveMonotoneX)
            );
        g.append('path')
            .datum(this.points)
            .attr('fill', 'none')
            .attr('stroke', COLOR_TX)
            .attr('stroke-width', 2)
            .attr(
                'd',
                d3.line()
                    .defined((d: Point) => d.tx != null)
                    .x((d) => x(d.date))
                    .y((d) => y(d.tx as number))
                    .curve(d3.curveMonotoneX)
            );

        // Rx area + line
        g.append('path')
            .datum(this.points)
            .attr('fill', COLOR_RX_FILL)
            .attr(
                'd',
                d3.area()
                    .defined((d: Point) => d.rx != null)
                    .x((d) => x(d.date))
                    .y0(innerH)
                    .y1((d) => y(d.rx as number))
                    .curve(d3.curveMonotoneX)
            );
        g.append('path')
            .datum(this.points)
            .attr('fill', 'none')
            .attr('stroke', COLOR_RX)
            .attr('stroke-width', 2)
            .attr(
                'd',
                d3.line()
                    .defined((d: Point) => d.rx != null)
                    .x((d) => x(d.date))
                    .y((d) => y(d.rx as number))
                    .curve(d3.curveMonotoneX)
            );

        // Header: title (left) + legend (right)
        const header = svg.append('g').attr('transform', `translate(${margin.left}, 16)`);
        if (this.title) {
            header.append('text').text(this.title)
                .attr('fill', COLOR_LEGEND_TEXT)
                .attr('font-size', '0.8125rem')
                .attr('font-weight', '600')
                .attr('dominant-baseline', 'middle');
        }
        const hasDowntime = this.parsedDowntime.length > 0;
        const hasMarkers = this.parsedMarkers.length > 0;
        const legendWidth = 110 + (hasDowntime ? 70 : 0) + (hasMarkers ? 60 : 0);
        const legendX = Math.max(0, innerW - legendWidth);
        const legend = header.append('g').attr('transform', `translate(${legendX}, -4)`);
        legend.append('rect').attr('width', 10).attr('height', 10).attr('rx', 2).attr('fill', COLOR_TX);
        legend.append('text').attr('x', 14).attr('y', 9).text(this.txLabel).attr('fill', COLOR_LEGEND_TEXT).attr('font-size', '0.75rem');
        legend.append('rect').attr('x', 46).attr('width', 10).attr('height', 10).attr('rx', 2).attr('fill', COLOR_RX);
        legend.append('text').attr('x', 60).attr('y', 9).text(this.rxLabel).attr('fill', COLOR_LEGEND_TEXT).attr('font-size', '0.75rem');
        let extraX = 96;
        if (hasDowntime) {
            legend.append('rect').attr('x', extraX).attr('width', 10).attr('height', 10).attr('rx', 2).attr('fill', 'rgba(244, 67, 54, 0.5)');
            legend.append('text').attr('x', extraX + 14).attr('y', 9).text('Offline').attr('fill', COLOR_LEGEND_TEXT).attr('font-size', '0.75rem');
            extraX += 64;
        }
        if (hasMarkers) {
            // Mirror the marker visual: a short amber dashed horizontal line rather than a
            // filled swatch, so the legend entry actually looks like what's drawn on the
            // chart. Centered vertically in the legend row (y=5 within the 10px height).
            legend.append('line')
                .attr('x1', extraX).attr('x2', extraX + 10)
                .attr('y1', 5).attr('y2', 5)
                .attr('stroke', 'rgba(245, 158, 11, 0.85)')
                .attr('stroke-width', 1.5)
                .attr('stroke-dasharray', '3,3');
            legend.append('text').attr('x', extraX + 14).attr('y', 9).text('Rekey').attr('fill', COLOR_LEGEND_TEXT).attr('font-size', '0.75rem');
        }

        // Hover
        const tooltip = this.tooltipEl.nativeElement;
        const bisect = d3.bisector((d: Point) => d.date).left;
        const focusLine = g.append('line')
            .attr('stroke', COLOR_AXIS)
            .attr('stroke-dasharray', '3,3')
            .attr('y1', 0)
            .attr('y2', innerH)
            .style('display', 'none');
        const focusTx = g.append('circle').attr('r', 3.5).attr('fill', COLOR_TX).style('display', 'none');
        const focusRx = g.append('circle').attr('r', 3.5).attr('fill', COLOR_RX).style('display', 'none');
        const overlay = g.append('rect')
            .attr('width', innerW)
            .attr('height', innerH)
            .attr('fill', 'none')
            .attr('pointer-events', 'all');

        overlay.on('mousemove', (event: MouseEvent) => {
            const [mx] = d3.pointer(event);
            const date = x.invert(mx);
            const idx = bisect(this.points, date, 1);
            const d0 = this.points[idx - 1];
            const d1 = this.points[idx];
            if (!d0) return;
            const d: Point =
                d1 && date.getTime() - d0.date.getTime() > d1.date.getTime() - date.getTime() ? d1 : d0;
            const px = x(d.date);

            focusLine.attr('x1', px).attr('x2', px).style('display', null);
            // Hide the per-series focus dot when that series has no value at the
            // hovered bucket — otherwise we'd draw a circle at y(null) which is
            // either at 0 (misleading) or NaN (renders at viewport origin).
            if (d.tx != null) {
                focusTx.attr('cx', px).attr('cy', y(d.tx)).style('display', null);
            } else {
                focusTx.style('display', 'none');
            }
            if (d.rx != null) {
                focusRx.attr('cx', px).attr('cy', y(d.rx)).style('display', null);
            } else {
                focusRx.style('display', 'none');
            }

            tooltip.style.display = 'block';
            tooltip.innerHTML =
                `<div>${d.date.toLocaleString()}</div>` +
                `<div style="color:${COLOR_TX}">${this.txLabel}: ${d.tx == null ? '—' : this.formatValue(d.tx)}</div>` +
                `<div style="color:${COLOR_RX}">${this.rxLabel}: ${d.rx == null ? '—' : this.formatValue(d.rx)}</div>`;

            const tooltipW = tooltip.offsetWidth || 140;
            const wrapW = wrapper.clientWidth;
            let left = margin.left + px + 12;
            if (left + tooltipW > wrapW) left = margin.left + px - tooltipW - 12;
            tooltip.style.left = `${left}px`;
            tooltip.style.top = `${margin.top + 4}px`;
        });

        overlay.on('mouseleave', () => {
            focusLine.style('display', 'none');
            focusTx.style('display', 'none');
            focusRx.style('display', 'none');
            tooltip.style.display = 'none';
        });
    }
}
